import { Trans, useTranslation } from "react-i18next";
import {
  IconCheck,
  IconHeartHandshake,
  IconMail,
  IconMicroscope,
  IconShieldLock,
} from "@tabler/icons-react";
import { cn } from "../../lib/utils";

/**
 * The body of the objection screen at signup (Figma O1): three lines that
 * explain, then the one legal part, the objection box. The box is never
 * checked when the screen opens (INV-06), and "Continue" works with the box
 * checked or not, so the caller owns the button. An existing user gets the
 * information email instead (docs/partner-objection.md, REQ-03).
 *
 * A desktop shows the same text in its own type (Figma D7, 5588:236987, the
 * last onboarding step): no hero icon, the title in the serif, and the box
 * without its card.
 */
export function PartnerObjectionContent({
  layout,
  objected,
  onObjectedChange,
  onOpenPartners,
  onOpenPrivacy,
}: {
  layout: "mobile" | "desktop";
  objected: boolean;
  onObjectedChange: (objected: boolean) => void;
  onOpenPartners: () => void;
  onOpenPrivacy: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const isDesktop = layout === "desktop";

  const rows = [
    {
      Icon: IconMicroscope,
      title: t("They fund rooms, not the science"),
      body: (
        <Trans
          i18nKey="<partnersLink>Healthcare companies and medical societies</partnersLink> support some rooms. Our independent medical team oversees the AI and ensures a balanced selection of studies."
          components={{
            partnersLink: (
              <button
                type="button"
                onClick={onOpenPartners}
                className={cn(
                  "inline cursor-pointer border-none bg-transparent p-0 text-left underline",
                  isDesktop
                    ? "font-medium text-ds-mint-800"
                    : "text-ds-mint-700"
                )}
              />
            ),
          }}
        />
      ),
    },
    {
      Icon: IconMail,
      title: t("Only the rooms you join"),
      body: t(
        "When you join a partner-supported room, the partner receives your email address and may contact you, for example to invite you to conferences, webinars or scientific events."
      ),
    },
    {
      Icon: IconShieldLock,
      title: t("Your email, your choice"),
      body: t(
        "You stay in control of sharing your email: change it anytime in Settings, with no effect on your experience."
      ),
    },
  ];

  return (
    <div
      className={cn("flex flex-col items-start", isDesktop ? "gap-7" : "gap-5")}
    >
      {!isDesktop && (
        <span className="flex size-14 items-center justify-center rounded-2xl bg-ds-moss-100">
          <IconHeartHandshake
            className="size-7 text-ds-mint-800"
            stroke={1.5}
          />
        </span>
      )}

      <h1
        className={cn(
          "m-0",
          !isDesktop
            ? "font-dm-sans text-heading-display font-semibold text-black"
            : "font-petrona text-desktop-title font-medium text-ds-mint-900"
        )}
      >
        {t("A word about our partners")}
      </h1>

      <div
        className={cn("flex w-full flex-col", isDesktop ? "gap-6" : "gap-4")}
      >
        {rows.map((row) => (
          <div key={row.title} className="flex items-start gap-3.5">
            <span
              className={cn(
                "flex shrink-0 items-center justify-center",
                isDesktop
                  ? "rounded-xl bg-ds-slate-100 p-2"
                  : "size-9.5 rounded-lg bg-ds-black/6"
              )}
            >
              <row.Icon
                className={cn(
                  "size-5",
                  isDesktop ? "text-ds-slate-900" : "text-ds-moss-600"
                )}
                stroke={1.5}
              />
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <h2
                className={cn(
                  "m-0 font-dm-sans",
                  isDesktop
                    ? "text-base font-semibold text-ds-black"
                    : "text-base font-bold leading-[1.4] text-ds-mint-800"
                )}
              >
                {row.title}
              </h2>
              <p
                className={cn(
                  "m-0 font-dm-sans font-normal",
                  isDesktop
                    ? "text-sm text-ds-slate-700"
                    : "text-footnote text-ds-slate-600"
                )}
              >
                {row.body}
              </p>
            </div>
          </div>
        ))}
      </div>

      <label
        className={cn(
          "flex w-full cursor-pointer items-start gap-3",
          isDesktop ? "pt-1" : "rounded-xl bg-white p-4"
        )}
      >
        <input
          type="checkbox"
          className="peer sr-only"
          checked={objected}
          onChange={(event) => {
            onObjectedChange(event.target.checked);
          }}
        />
        <span
          aria-hidden
          className={cn(
            "flex shrink-0 items-center justify-center border-2 peer-focus-visible:shadow-focus-ring",
            isDesktop ? "mt-0.5 size-4.5 rounded-sm" : "size-5 rounded-md",
            objected
              ? "border-ds-primary bg-ds-primary"
              : "border-ds-slate-300 bg-white"
          )}
        >
          {objected && <IconCheck className="size-3.5 text-white" stroke={3} />}
        </span>
        <span
          className={cn(
            "flex-1 font-dm-sans text-ui-body text-ds-slate-900",
            isDesktop ? "font-normal" : "font-medium"
          )}
        >
          {t(
            "I object to my email address being shared with Juisci's partners for professional communications."
          )}
        </span>
      </label>

      <div className="flex w-full justify-between font-dm-sans text-footnote font-medium">
        <button
          type="button"
          onClick={onOpenPrivacy}
          className={cn(
            "cursor-pointer border-none bg-transparent p-0 text-ds-mint-700",
            isDesktop && "underline"
          )}
        >
          {t("Privacy policy")}
        </button>
        <button
          type="button"
          onClick={onOpenPartners}
          className={cn(
            "cursor-pointer border-none bg-transparent p-0 text-ds-mint-700",
            isDesktop && "underline"
          )}
        >
          {t("See our partners")}
        </button>
      </div>
    </div>
  );
}
